import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useEffect } from "react";
import MealRemindersCubit from "src/state/MealRemindersCubit/MealRemindersCubit";
import MealReminderFlowCubit from "src/ui/components/MealReminders/MealReminderFlowCubit";
import AppDebugCubit from "./AppDebugCubit";
import {
  DebugBadge,
  DebugButton,
  DebugButtonRow,
  DebugInfoBox,
  DebugKeyValue,
  DebugSubheading
} from "./debug.styles";

const Explanation = styled(DebugInfoBox)`
  font-size: 12px;
`;

const ActionRow = styled(DebugButtonRow)`
  margin-top: 0.75rem;
`;

const DebugMealReminders: FC = () => {
  const [
    { phase, remindersOn, calibrationDaysLogged, mealsToday, logOverview },
    { load, debugReset }
  ] = useBlocNext(MealRemindersCubit);
  const [, { debugShowPrompt, openEditor, openDownloadSheet }] = useBlocNext(
    MealReminderFlowCubit
  );
  const [, { setMinified }] = useBlocNext(AppDebugCubit);

  const withMinify = (open: () => void) => (): void => {
    open();
    setMinified(true);
  };

  useEffect(() => {
    void load();
  }, [load]);

  const loggedDates = logOverview
    .filter((day) => day.logged)
    .map((day) => day.date)
    .join(", ");

  return (
    <div>
      <DebugSubheading>Meal reminders</DebugSubheading>
      <DebugKeyValue>
        <span className="key">Phase</span>
        <span className="value">
          <DebugBadge variant="info">{phase}</DebugBadge>
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Reminders on</span>
        <span className="value">
          <DebugBadge variant={remindersOn ? "success" : "info"}>
            {String(remindersOn)}
          </DebugBadge>
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Calibration days logged</span>
        <span className="value">{calibrationDaysLogged}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Meals today</span>
        <span className="value">{mealsToday}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Logged in strip</span>
        <span className="value">{loggedDates || "none"}</span>
      </DebugKeyValue>

      <Explanation variant="info">
        <p>
          Calibration runs for 7 days from the member&apos;s first meal ever,
          whether or not reminders are on. To restart it, delete every logged
          meal. Reset restores the backend defaults (reminders off) and forgets
          the local day-7 choice.
        </p>
      </Explanation>

      <ActionRow>
        <DebugButton className="small" onClick={() => void debugReset()}>
          Reset reminders
        </DebugButton>
      </ActionRow>

      <Explanation variant="info">
        <p>
          These open the reminder screens directly and collapse this panel so
          they&apos;re visible. The prompt is what members see after their first
          meal of the day (this also clears &ldquo;Not Now&rdquo;). The editor
          is where they pick meal times. The download sheet is what mobile-web
          members get instead of the editor.
        </p>
      </Explanation>

      <ActionRow>
        <DebugButton className="small" onClick={withMinify(debugShowPrompt)}>
          Show reminder prompt
        </DebugButton>
        <DebugButton className="small" onClick={withMinify(openEditor)}>
          Open time editor
        </DebugButton>
        <DebugButton className="small" onClick={withMinify(openDownloadSheet)}>
          Open get-the-app sheet
        </DebugButton>
      </ActionRow>
    </div>
  );
};

export default DebugMealReminders;
